export type DiffLine = { kind: "same" | "add" | "del"; text: string };

/**
 * Diff por linha (LCS clássico) entre dois prompts. Quadrático em linhas, o que
 * para prompt de até algumas centenas de linhas é instantâneo; acima de um teto
 * cai para "tudo removido / tudo adicionado" em vez de travar a aba.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split("\n");
  const b = after.split("\n");
  const MAX_CELLS = 4_000_000;

  if (a.length * b.length > MAX_CELLS) {
    return [
      ...a.map((text) => ({ kind: "del" as const, text })),
      ...b.map((text) => ({ kind: "add" as const, text })),
    ];
  }

  // lcs[i][j] = tamanho da subsequência comum de a[i..] e b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0)
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: "del", text: a[i] });
      i++;
    } else {
      out.push({ kind: "add", text: b[j] });
      j++;
    }
  }
  while (i < a.length) out.push({ kind: "del", text: a[i++] });
  while (j < b.length) out.push({ kind: "add", text: b[j++] });
  return out;
}

export type DiffSegment = DiffLine | { kind: "gap"; lines: DiffLine[] };

/**
 * Recolhe as linhas iguais longe das mudanças: em volta de cada mudança ficam
 * `context` linhas, o resto vira um trecho (`gap`) que a tela expande. Trecho
 * de uma linha só não é recolhido (o botão ocuparia o mesmo espaço).
 */
export function collapseDiff(
  lines: DiffLine[],
  context: number
): DiffSegment[] {
  const segments: DiffSegment[] = [];
  let index = 0;
  while (index < lines.length) {
    if (lines[index].kind !== "same") {
      segments.push(lines[index]);
      index += 1;
      continue;
    }
    let end = index;
    while (end < lines.length && lines[end].kind === "same") end += 1;
    const run = lines.slice(index, end);
    const keepBefore = index > 0 ? context : 0;
    const keepAfter = end < lines.length ? context : 0;
    const hidden = run.length - keepBefore - keepAfter;
    if (hidden < 2) {
      segments.push(...run);
    } else {
      segments.push(
        ...run.slice(0, keepBefore),
        { kind: "gap", lines: run.slice(keepBefore, run.length - keepAfter) },
        ...run.slice(run.length - keepAfter)
      );
    }
    index = end;
  }
  return segments;
}
